import { useMemo } from "react";
import { Instagram, ExternalLink } from "lucide-react";

/**
 * Curated Instagram feed for the landing page.
 * Uses Instagram's official `/embed` iframe endpoint — no JS bundle to load,
 * no window globals, and no CSP surprises. Each iframe is a self-contained
 * post/reel card that Instagram serves directly.
 *
 * Admins paste post/reel URLs in the Admin → Settings tab. No API key needed.
 * If Instagram is unreachable, the surrounding link still lets viewers open
 * the post directly.
 */
export default function InstagramFeed({ posts = [], username, className = "" }) {
  const items = useMemo(
    () =>
      (posts || [])
        .map((raw) => {
          const info = parseInstagramUrl(raw);
          return info ? { ...info, raw } : null;
        })
        .filter(Boolean),
    [posts],
  );

  if (items.length === 0) return null;

  const handle = username && username.trim()
    ? username.replace(/^https?:\/\/(www\.)?instagram\.com\//i, "").replace(/\/+$/, "")
    : "odishasteelcityrunners";
  const profileUrl = username || `https://www.instagram.com/${handle}`;

  return (
    <section
      data-testid="instagram-feed"
      className={`relative border-t border-zinc-800 bg-zinc-950 ${className}`}
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-20">
        <div className="flex items-end justify-between gap-6 flex-wrap mb-10">
          <div>
            <p className="font-mono text-[11px] tracking-[0.35em] text-orange-500 uppercase inline-flex items-center gap-2">
              <Instagram size={12} /> ON INSTAGRAM
            </p>
            <h2 className="font-editorial text-4xl sm:text-5xl lg:text-6xl text-white mt-3 leading-[0.92] tracking-tight">
              Fresh from <span className="text-orange-500">@{handle}.</span>
            </h2>
            <p className="text-zinc-400 mt-3 max-w-xl">
              Race photos, community moments, and morning-run reels — straight from the OSCR feed.
            </p>
          </div>
          <a
            href={profileUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="instagram-follow-cta"
            className="font-mono text-[11px] tracking-[0.3em] text-orange-500 hover:text-orange-400 uppercase inline-flex items-center gap-1.5"
          >
            FOLLOW ON INSTAGRAM <ExternalLink size={13} />
          </a>
        </div>

        <div
          data-testid="instagram-embed-grid"
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4"
        >
          {items.map((it) => (
            <div
              key={it.raw}
              data-testid={`instagram-post-${it.shortcode}`}
              className="hairline bg-white overflow-hidden group card-editorial"
            >
              <div className="relative w-full" style={{ aspectRatio: "1 / 1.28" }}>
                <iframe
                  src={`https://www.instagram.com/${it.kind}/${it.shortcode}/embed/`}
                  title={`Instagram ${it.kind} ${it.shortcode}`}
                  loading="lazy"
                  scrolling="no"
                  allow="encrypted-media; picture-in-picture; web-share"
                  allowFullScreen
                  frameBorder="0"
                  className="absolute inset-0 w-full h-full"
                />
              </div>
              <a
                href={it.raw}
                target="_blank"
                rel="noopener noreferrer"
                className="block px-4 py-2.5 bg-zinc-900 border-t border-zinc-800 font-mono text-[10px] tracking-[0.3em] text-zinc-400 uppercase hover:text-orange-500 hover:border-orange-600/40 transition"
              >
                OPEN ON INSTAGRAM →
              </a>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/**
 * Extracts (kind, shortcode) from a raw Instagram URL.
 * Supports posts (/p/…), reels (/reel/…), and TV (/tv/…).
 * Returns null if the URL doesn't match a known Instagram content pattern.
 */
function parseInstagramUrl(url) {
  if (!url || typeof url !== "string") return null;
  const m = url.match(/instagram\.com\/(p|reel|tv)\/([a-zA-Z0-9_-]+)/i);
  if (!m) return null;
  return { kind: m[1].toLowerCase(), shortcode: m[2] };
}
